import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAdminNotifications } from '../api';

const SEVERITY_STYLES = {
  error:   { bg: '#FFEBEE', border: '#E74C3C', color: '#C62828', icon: '\u26A0' },
  warning: { bg: '#FFF8E1', border: '#F39C12', color: '#E65100', icon: '\u25CF' },
  info:    { bg: '#E3F2FD', border: '#2E75B6', color: '#1565C0', icon: '\u25CF' },
};

const CATEGORY_ICONS = {
  'NDA': '\uD83D\uDCC4',
  'Data Room': '\uD83D\uDCC1',
  'Dossiers': '\uD83D\uDCCB',
  'Audit': '\uD83D\uDD0D',
  'Utilisateurs': '\uD83D\uDC64',
  'Trophées': '\uD83C\uDFC6',
  'QIT': '\u2705',
};

const CATEGORY_FILTERS = [
  { key: 'all', label: 'Tout' },
  { key: 'action', label: 'Actions requises' },
  { key: 'NDA', label: 'NDA' },
  { key: 'Data Room', label: 'Data Room' },
  { key: 'Dossiers', label: 'Dossiers' },
  { key: 'Audit', label: 'Audit' },
  { key: 'Utilisateurs', label: 'Utilisateurs' },
  { key: 'Trophées', label: 'Trophées' },
  { key: 'QIT', label: 'QIT' },
];

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diffMs = now - date;
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "à l'instant";
  if (diffMin < 60) return `il y a ${diffMin} min`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `il y a ${diffH}h`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return 'hier';
  if (diffD < 7) return `il y a ${diffD}j`;
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
}

export default function NotificationCenter({ onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const panelRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      const result = await getAdminNotifications();
      setData(result);
    } catch (err) {
      console.error('Notification load error:', err);
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 60s
  useEffect(() => {
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  // Close on click outside
  useEffect(() => {
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        onClose();
      }
    };
    // Delay to avoid immediate close from the bell click
    const timer = setTimeout(() => document.addEventListener('mousedown', handleClick), 100);
    return () => { clearTimeout(timer); document.removeEventListener('mousedown', handleClick); };
  }, [onClose]);

  // Close on Escape
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const handleNavigate = (link) => {
    if (link) navigate(link);
    onClose();
  };

  const notifications = data?.notifications || [];
  const summary = data?.summary || {};

  const filtered = filter === 'all'
    ? notifications
    : filter === 'action'
      ? notifications.filter(n => n.severity === 'error' || n.severity === 'warning')
      : notifications.filter(n => n.category === filter);

  const actionCount = notifications.filter(n => n.severity === 'error' || n.severity === 'warning').length;

  return (
    <div ref={panelRef} style={{
      position: 'absolute', top: '100%', right: 0, marginTop: 8,
      width: 460, maxHeight: 'calc(100vh - 100px)',
      background: 'white', borderRadius: 12, boxShadow: '0 8px 40px rgba(0,0,0,0.18)',
      display: 'flex', flexDirection: 'column', overflow: 'hidden',
      animation: 'notifSlideIn 0.2s ease', zIndex: 10002,
    }}>
      {/* Header */}
      <div style={{ padding: '16px 20px', borderBottom: '1px solid #F0F0F0', flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <h3 style={{ margin: 0, fontSize: 16, color: '#333' }}>Centre de notifications</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#999', padding: 0 }}>
            {'\u2715'}
          </button>
        </div>

        {/* Summary badges */}
        {!loading && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {summary.critical > 0 && (
              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#FFEBEE', color: '#C62828' }}>
                {summary.critical} critique{summary.critical > 1 ? 's' : ''}
              </span>
            )}
            {summary.nda_pending > 0 && (
              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#FFF8E1', color: '#E65100' }}>
                {summary.nda_pending} NDA
              </span>
            )}
            {summary.documents_pending > 0 && (
              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#E3F2FD', color: '#1565C0' }}>
                {summary.documents_pending} document{summary.documents_pending > 1 ? 's' : ''}
              </span>
            )}
            {summary.dossiers_pending > 0 && (
              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#FFF3E0', color: '#E65100' }}>
                {summary.dossiers_pending} dossier{summary.dossiers_pending > 1 ? 's' : ''}
              </span>
            )}
            {summary.audit_alerts > 0 && (
              <span style={{ padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600, background: '#FCE4EC', color: '#C62828' }}>
                {summary.audit_alerts} alerte{summary.audit_alerts > 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}
      </div>

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 0, borderBottom: '1px solid #F0F0F0', overflowX: 'auto', flexShrink: 0 }}>
        {CATEGORY_FILTERS.map(f => {
          const count = f.key === 'all' ? notifications.length
            : f.key === 'action' ? actionCount
            : notifications.filter(n => n.category === f.key).length;
          if (count === 0 && f.key !== 'all' && f.key !== 'action') return null;
          return (
            <button key={f.key} onClick={() => setFilter(f.key)}
              style={{
                padding: '8px 14px', border: 'none', background: 'none', cursor: 'pointer',
                fontFamily: 'inherit', fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
                borderBottom: `2px solid ${filter === f.key ? 'var(--primary-light)' : 'transparent'}`,
                color: filter === f.key ? 'var(--primary)' : '#999',
              }}>
              {f.label}{count > 0 ? ` (${count})` : ''}
            </button>
          );
        })}
      </div>

      {/* Notification list */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '8px 0' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999' }}>
            <div style={{ width: 20, height: 20, border: '2px solid #ccc', borderTop: '2px solid var(--primary)',
              borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
            Chargement...
          </div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#999', fontSize: 14 }}>
            {filter === 'all' ? 'Aucune notification' : 'Aucune notification dans cette catégorie'}
          </div>
        ) : (
          filtered.map(n => {
            const sev = SEVERITY_STYLES[n.severity] || SEVERITY_STYLES.info;
            const catIcon = CATEGORY_ICONS[n.category] || '\uD83D\uDD14';
            return (
              <div key={n.id}
                onClick={() => handleNavigate(n.link)}
                style={{
                  display: 'flex', gap: 12, padding: '12px 20px',
                  cursor: n.link ? 'pointer' : 'default',
                  borderLeft: `3px solid ${sev.border}`,
                  marginBottom: 1,
                  background: 'white',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => e.currentTarget.style.background = '#FAFAFA'}
                onMouseLeave={e => e.currentTarget.style.background = 'white'}>
                {/* Icon */}
                <div style={{
                  width: 36, height: 36, borderRadius: 8,
                  background: sev.bg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 16, flexShrink: 0,
                }}>
                  {catIcon}
                </div>
                {/* Content */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
                    <div style={{ fontSize: 12, fontWeight: 600, color: sev.color }}>{n.title}</div>
                    <div style={{ fontSize: 10, color: '#BBB', whiteSpace: 'nowrap', flexShrink: 0 }}>{timeAgo(n.created_at)}</div>
                  </div>
                  <div style={{ fontSize: 12, color: '#555', marginTop: 2, lineHeight: 1.4,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {n.message}
                  </div>
                  <span style={{
                    display: 'inline-block', marginTop: 4,
                    padding: '1px 8px', borderRadius: 8, fontSize: 9, fontWeight: 600,
                    background: '#F5F5F5', color: '#999',
                  }}>{n.category}</span>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer */}
      {!loading && notifications.length > 0 && (
        <div style={{
          padding: '10px 20px', borderTop: '1px solid #F0F0F0', flexShrink: 0,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{ fontSize: 11, color: '#999' }}>
            {notifications.length} notification{notifications.length > 1 ? 's' : ''} au total
          </span>
          <button onClick={() => handleNavigate('/analyst/dossiers')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--primary)', fontWeight: 600, fontFamily: 'inherit' }}>
            Voir tous les dossiers
          </button>
        </div>
      )}

      {/* CSS animation */}
      <style>{`
        @keyframes notifSlideIn {
          from { opacity: 0; transform: translateY(-8px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

// Bell icon component for the navbar
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [count, setCount] = useState(0);

  const loadCount = useCallback(async () => {
    try {
      const result = await getAdminNotifications();
      // Count only actionable items (warnings + errors)
      const actionable = (result.summary?.nda_pending || 0)
        + (result.summary?.documents_pending || 0)
        + (result.summary?.audit_alerts || 0)
        + (result.summary?.critical || 0);
      setCount(actionable);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadCount();
    const interval = setInterval(loadCount, 60000);
    return () => clearInterval(interval);
  }, [loadCount]);

  return (
    <div style={{ position: 'relative' }}>
      <button onClick={() => setOpen(!open)}
        style={{
          background: open ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.1)',
          border: 'none', cursor: 'pointer', borderRadius: 8,
          width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center',
          position: 'relative', transition: 'background 0.2s',
        }}
        onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.2)'}
        onMouseLeave={e => { if (!open) e.currentTarget.style.background = 'rgba(255,255,255,0.1)'; }}
        title="Centre de notifications">
        {/* Bell SVG */}
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
        {/* Badge */}
        {count > 0 && (
          <span style={{
            position: 'absolute', top: 2, right: 2,
            background: '#E74C3C', color: 'white',
            fontSize: 9, fontWeight: 700,
            minWidth: 16, height: 16, borderRadius: 8,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '0 4px', lineHeight: 1,
            animation: 'notifPulse 2s ease-in-out infinite',
          }}>
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && <NotificationCenter onClose={() => { setOpen(false); loadCount(); }} />}

      <style>{`
        @keyframes notifPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.1); }
        }
      `}</style>
    </div>
  );
}
